import React, { useState, useEffect } from 'react';
import { contactsApi, dealsApi } from '../services/api';
import { 
  Users, 
  Plus, 
  Search, 
  Upload,
  X,
  MoreHorizontal,
  Edit2,
  Trash2,
  ChevronDown,
  Building2,
  Mail,
  Phone,
  CheckCircle2,
  XCircle
} from 'lucide-react';
import './Contacts.css';



const contactTypes = [
  { id: 'ceo', name: 'CEO' },
  { id: 'cfo', name: 'CFO' },
  { id: 'coo', name: 'COO' },
  { id: 'legal', name: 'Legal' },
  { id: 'operations', name: 'Operations' },
  { id: 'sales', name: 'Sales' },
  { id: 'other', name: 'Other' }
];

const statuses = [
  { id: 'active', name: 'Active', color: '#059669' },
  { id: 'inactive', name: 'Inactive', color: '#6B7280' }
];

function Contacts({ onNavigateToDeal }) {
  const [contacts, setContacts] = useState([]);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    type: 'other',
    dealId: '',
    email: '',
    phone: '',
    status: 'active',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [contactsRes, dealsRes] = await Promise.all([
        contactsApi.list(),
        dealsApi.list()
      ]);
      setContacts(contactsRes.data || []);
      setDeals(dealsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateContact = async () => {
    if (!formData.name.trim() || !formData.email.trim()) return;
    
    try {
      await contactsApi.create(formData);
      resetForm();
      setShowAddModal(false);
      fetchData();
    } catch (error) {
      console.error('Failed to create contact:', error);
      alert('Failed to create contact');
    }
  };

  const handleUpdateContact = async () => {
    if (!editingContact) return;
    
    try {
      await contactsApi.update(editingContact.id, formData);
      resetForm();
      setShowEditModal(false);
      setEditingContact(null);
      fetchData();
    } catch (error) {
      console.error('Failed to update contact:', error);
      alert('Failed to update contact');
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    
    try {
      await contactsApi.delete(contactId);
      fetchData();
    } catch (error) {
      console.error('Failed to delete contact:', error);
      alert('Failed to delete contact');
    }
  };

  const handleImportCSV = async () => {
    if (!importPreview || importPreview.length === 0) return;
    
    setImporting(true);
    try {
      await contactsApi.import(importPreview);
      setImportPreview(null);
      setShowImportModal(false);
      fetchData();
    } catch (error) {
      console.error('Failed to import contacts:', error);
      alert('Failed to import contacts');
    } finally {
      setImporting(false);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      parseCSV(text);
    };
    reader.readAsText(file);
  };

  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      alert('CSV file must have a header row and at least one data row');
      return;
    }
    
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const nameIdx = headers.findIndex(h => h.includes('name'));
    const emailIdx = headers.findIndex(h => h.includes('email'));
    const typeIdx = headers.findIndex(h => h.includes('type') || h.includes('role'));
    const dealIdx = headers.findIndex(h => h.includes('deal') || h.includes('entity') || h.includes('company'));
    const phoneIdx = headers.findIndex(h => h.includes('phone'));
    const statusIdx = headers.findIndex(h => h.includes('status'));
    const notesIdx = headers.findIndex(h => h.includes('notes') || h.includes('comment'));
    
    if (nameIdx === -1 || emailIdx === -1) {
      alert('CSV must have Name and Email columns');
      return;
    }
    
    const parsed = [];
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      if (values[nameIdx]) {
        parsed.push({
          name: values[nameIdx],
          email: values[emailIdx] || '',
          type: values[typeIdx] || 'other',
          dealId: dealIdx >= 0 ? findDealIdByName(values[dealIdx]) : '',
          phone: phoneIdx >= 0 ? values[phoneIdx] : '',
          status: (statusIdx >= 0 ? values[statusIdx] : 'active').toLowerCase(),
          notes: notesIdx >= 0 ? values[notesIdx] : ''
        });
      }
    }
    
    setImportPreview(parsed);
  };

  const findDealIdByName = (name) => {
    if (!name) return '';
    const deal = deals.find(d => d.name.toLowerCase() === name.toLowerCase());
    return deal ? deal.id : '';
  };

  const getDealName = (dealId) => {
    if (!dealId) return '—';
    const deal = deals.find(d => d.id === dealId);
    return deal ? deal.name : '—';
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'other',
      dealId: '',
      email: '',
      phone: '',
      status: 'active',
      notes: ''
    });
  };

  const openEditModal = (contact) => {
    setEditingContact(contact);
    setFormData({
      name: contact.name,
      type: contact.type,
      dealId: contact.dealId || '',
      email: contact.email,
      phone: contact.phone || '',
      status: contact.status,
      notes: contact.notes || ''
    });
    setShowEditModal(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = statuses.find(s => s.id === status) || statuses[0];
    return (
      <span 
        className="contact-status-badge"
        style={{ 
          backgroundColor: `${statusConfig.color}15`,
          color: statusConfig.color
        }}
      >
        {status === 'active' ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
        {statusConfig.name}
      </span>
    );
  };

  const getTypeLabel = (type) => {
    const typeConfig = contactTypes.find(t => t.id === type);
    return typeConfig ? typeConfig.name : type;
  };

  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = contact.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         contact.email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = typeFilter === 'all' || contact.type === typeFilter;
    const matchesStatus = statusFilter === 'all' || contact.status === statusFilter;
    return matchesSearch && matchesType && matchesStatus;
  });

  if (loading) {
    return (
      <div className="contacts-container">
        <div className="contacts-loading">
          <div className="contacts-spinner"></div>
          <p>Loading contacts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="contacts-container">
      {/* Header */}
      <div className="contacts-header">
        <div className="contacts-title-section">
          <h1 className="contacts-title">Contacts</h1>
          <span className="contacts-count">{contacts.length} contact{contacts.length !== 1 ? 's' : ''}</span>
        </div>
        <div className="contacts-header-actions">
          <button 
            className="contacts-import-btn"
            onClick={() => setShowImportModal(true)}
          >
            <Upload size={18} />
            <span>Import CSV</span>
          </button>
          <button 
            className="contacts-new-btn"
            onClick={() => setShowAddModal(true)}
          >
            <Plus size={18} />
            <span>Add Contact</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="contacts-filters">
        <div className="contacts-search">
          <Search size={16} className="contacts-search-icon" />
          <input
            type="text"
            placeholder="Search contacts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="contacts-filter-dropdown">
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="all">All Types</option>
            {contactTypes.map(type => (
              <option key={type.id} value={type.id}>{type.name}</option>
            ))}
          </select>
          <ChevronDown size={16} className="contacts-filter-chevron" />
        </div>
        <div className="contacts-filter-dropdown">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All Statuses</option>
            {statuses.map(status => (
              <option key={status.id} value={status.id}>{status.name}</option>
            ))}
          </select>
          <ChevronDown size={16} className="contacts-filter-chevron" />
        </div>
      </div>

      {/* Empty State */}
      {contacts.length === 0 ? (
        <div className="contacts-empty">
          <div className="contacts-empty-icon">
            <Users size={48} />
          </div>
          <h3>No contacts yet</h3>
          <p>Add your first contact or import from CSV</p>
          <div className="contacts-empty-actions">
            <button className="contacts-import-btn" onClick={() => setShowImportModal(true)}>
              <Upload size={18} />
              Import CSV
            </button>
            <button className="contacts-new-btn" onClick={() => setShowAddModal(true)}>
              <Plus size={18} />
              Add Contact
            </button>
          </div>
        </div>
      ) : (
        /* Contacts Table */
        <div className="contacts-table-container">
          <table className="contacts-table">
            <thead>
              <tr>
                <th className="contacts-th-name">Name</th>
                <th className="contacts-th-type">Type</th>
                <th className="contacts-th-entity">Entity</th>
                <th className="contacts-th-email">Email</th>
                <th className="contacts-th-status">Status</th>
                <th className="contacts-th-notes">Notes</th>
                <th className="contacts-th-updated">Updated</th>
                <th className="contacts-th-actions"></th>
              </tr>
            </thead>
            <tbody>
              {filteredContacts.map((contact) => (
                <tr key={contact.id} className="contacts-row">
                  <td className="contacts-td-name">
                    <div className="contacts-name-cell">
                      <div className="contacts-avatar">
                        {contact.name?.charAt(0).toUpperCase()}
                      </div>
                      <span className="contacts-name-text">{contact.name}</span>
                    </div>
                  </td>
                  <td className="contacts-td-type">
                    <span className="contacts-type-badge">{getTypeLabel(contact.type)}</span>
                  </td>
                  <td className="contacts-td-entity">
                    {contact.dealId ? (
                      <button 
                        className="contacts-deal-link"
                        onClick={() => onNavigateToDeal?.(contact.dealId)}
                      >
                        <Building2 size={14} />
                        {getDealName(contact.dealId)}
                      </button>
                    ) : (
                      <span className="contacts-unlinked">—</span>
                    )}
                  </td>
                  <td className="contacts-td-email">
                    <a href={`mailto:${contact.email}`} className="contacts-email-link">
                      <Mail size={14} />
                      {contact.email}
                    </a>
                  </td>
                  <td className="contacts-td-status">
                    {getStatusBadge(contact.status)}
                  </td>
                  <td className="contacts-td-notes">
                    <NotesCell 
                      contact={contact} 
                      onUpdate={fetchData}
                    />
                  </td>
                  <td className="contacts-td-updated">
                    {formatDate(contact.updatedAt)}
                  </td>
                  <td className="contacts-td-actions">
                    <button 
                      className="contacts-action-btn"
                      onClick={() => openEditModal(contact)}
                      title="Edit"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button 
                      className="contacts-action-btn delete"
                      onClick={() => handleDeleteContact(contact.id)}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Contact Modal */}
      {showAddModal && (
        <ContactModal
          title="Add Contact"
          formData={formData}
          setFormData={setFormData}
          deals={deals}
          onSave={handleCreateContact}
          onCancel={() => {
            resetForm();
            setShowAddModal(false);
          }}
        />
      )}

      {/* Edit Contact Modal */}
      {showEditModal && (
        <ContactModal
          title="Edit Contact"
          formData={formData}
          setFormData={setFormData}
          deals={deals}
          onSave={handleUpdateContact}
          onCancel={() => {
            resetForm();
            setShowEditModal(false);
            setEditingContact(null);
          }}
        />
      )}

      {/* Import CSV Modal */}
      {showImportModal && (
        <div className="contacts-modal-overlay" onClick={() => setShowImportModal(false)}>
          <div className="contacts-modal contacts-modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="contacts-modal-header">
              <h2>Import Contacts</h2>
              <button className="contacts-modal-close" onClick={() => setShowImportModal(false)}>
                <X size={20} />
              </button>
            </div>
            
            {!importPreview ? (
              <div className="contacts-modal-body">
                <div className="contacts-import-upload">
                  <Upload size={48} className="contacts-import-icon" />
                  <h3>Upload CSV file</h3>
                  <p>Required columns: <strong>Name, Email</strong></p>
                  <p>Optional columns: Type, Entity/Deal, Phone, Status</p>
                  <label className="contacts-import-file-label">
                    <input 
                      type="file" 
                      accept=".csv" 
                      onChange={handleFileUpload}
                      className="contacts-import-file-input"
                    />
                    <span>Select File</span>
                  </label>
                </div>
              </div>
            ) : (
              <div className="contacts-modal-body">
                <h3>Preview ({importPreview.length} contacts)</h3>
                <div className="contacts-import-preview">
                  <table className="contacts-preview-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Type</th>
                        <th>Entity</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importPreview.slice(0, 10).map((contact, idx) => (
                        <tr key={idx}>
                          <td>{contact.name}</td>
                          <td>{contact.email}</td>
                          <td>{getTypeLabel(contact.type)}</td>
                          <td>{getDealName(contact.dealId) || contact.dealId || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {importPreview.length > 10 && (
                    <p className="contacts-preview-more">... and {importPreview.length - 10} more</p>
                  )}
                </div>
                <div className="contacts-modal-footer">
                  <button 
                    className="contacts-btn-secondary" 
                    onClick={() => setImportPreview(null)}
                  >
                    Back
                  </button>
                  <button 
                    className="contacts-btn-primary"
                    onClick={handleImportCSV}
                    disabled={importing}
                  >
                    {importing ? 'Importing...' : `Import ${importPreview.length} Contacts`}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Contact Modal Component
function ContactModal({ title, formData, setFormData, deals, onSave, onCancel }) {
  return (
    <div className="contacts-modal-overlay" onClick={onCancel}>
      <div className="contacts-modal" onClick={(e) => e.stopPropagation()}>
        <div className="contacts-modal-header">
          <h2>{title}</h2>
          <button className="contacts-modal-close" onClick={onCancel}>
            <X size={20} />
          </button>
        </div>
        
        <div className="contacts-modal-body">
          <div className="contacts-form-group">
            <label>Name *</label>
            <input
              type="text"
              placeholder="Enter contact name"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              autoFocus
            />
          </div>
          
          <div className="contacts-form-row">
            <div className="contacts-form-group">
              <label>Type</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({...formData, type: e.target.value})}
              >
                {contactTypes.map(type => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </select>
            </div>
            <div className="contacts-form-group">
              <label>Entity / Deal</label>
              <select
                value={formData.dealId}
                onChange={(e) => setFormData({...formData, dealId: e.target.value})}
              >
                <option value="">— Unlinked —</option>
                {deals.map(deal => (
                  <option key={deal.id} value={deal.id}>{deal.name}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="contacts-form-row">
            <div className="contacts-form-group">
              <label>Email *</label>
              <div className="contacts-input-with-icon">
                <Mail size={16} className="contacts-input-icon" />
                <input
                  type="email"
                  placeholder="email@company.com"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                />
              </div>
            </div>
            <div className="contacts-form-group">
              <label>Phone</label>
              <div className="contacts-input-with-icon">
                <Phone size={16} className="contacts-input-icon" />
                <input
                  type="tel"
                  placeholder="+1 (555) 000-0000"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                />
              </div>
            </div>
          </div>
          
          <div className="contacts-form-group">
            <label>Notes</label>
            <textarea
              placeholder="Add notes about your last contact..."
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              rows={3}
              className="contacts-notes-textarea"
            />
          </div>
          
          <div className="contacts-form-group">
            <label>Status</label>
            <div className="contacts-status-options">
              {statuses.map(status => (
                <label 
                  key={status.id} 
                  className={`contacts-status-option ${formData.status === status.id ? 'selected' : ''}`}
                  onClick={() => setFormData({...formData, status: status.id})}
                >
                  <input 
                    type="radio" 
                    name="status" 
                    value={status.id}
                    checked={formData.status === status.id}
                    onChange={() => setFormData({...formData, status: status.id})}
                  />
                  <span style={{ color: status.color }}>
                    {status.id === 'active' ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
                    {status.name}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>
        
        <div className="contacts-modal-footer">
          <button className="contacts-btn-secondary" onClick={onCancel}>
            Cancel
          </button>
          <button 
            className="contacts-btn-primary"
            onClick={onSave}
            disabled={!formData.name.trim() || !formData.email.trim()}
          >
            Save Contact
          </button>
        </div>
      </div>
    </div>
  );
}

// Notes Cell Component - Inline editable notes
function NotesCell({ contact, onUpdate }) {
  const [isEditing, setIsEditing] = useState(false);
  const [notes, setNotes] = useState(contact.notes || '');
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (notes === (contact.notes || '')) {
      setIsEditing(false);
      return;
    }
    
    setIsSaving(true);
    try {
      await contactsApi.update(contact.id, { notes: notes.trim() });
      setIsEditing(false);
      onUpdate();
    } catch (error) {
      console.error('Failed to update notes:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      setNotes(contact.notes || '');
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <div className="contacts-notes-edit">
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          placeholder="Add notes..."
          autoFocus
          disabled={isSaving}
        />
        <div className="contacts-notes-edit-hint">
          {isSaving ? 'Saving...' : 'Press Enter to save, Esc to cancel'}
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`contacts-notes-display ${!contact.notes ? 'empty' : ''}`}
      onClick={() => setIsEditing(true)}
      title="Click to edit notes"
    >
      {contact.notes || 'Add notes...'}
    </div>
  );
}

export default Contacts;
